import { useState, FormEvent } from 'react';
import { SEO } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'お問い合わせ - Contact',
    description: 'ステーブルコイン、クラティブ・健康DX、輸入商品、建設・防水コンクリートに関するご相談はお気軽にお問い合わせください。',
    url: `${siteUrl}/contact`
  };

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('category', formData.category);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4k0p3ueoa4vmlpb5igg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          category: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="お問い合わせ｜福岡株式会社ガイアライブラリー"
        description="ステーブルコイン、クラティブ・健康DX、輸入商品、建設・防水コンクリートに関するご相談はお気軽にお問い合わせください。福岡市中央区の株式会社ガイアライブラリーへのお問い合わせフォームです。"
        keywords="お問い合わせ,相談,コンタクト,福岡,ガイアライブラリー"
        schema={schema}
      />
      {/* Page Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20 px-6 md:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase mb-4 text-blue-200">Contact</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">お問い合わせ</h1>
          <p className="text-lg text-blue-100 leading-relaxed">
            ご質問・ご相談はお気軽にお問い合わせください
          </p>
        </div>
      </div>

      {/* Contact Form */}
      <section className="py-20 md:py-24 px-6 md:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-10 md:p-14">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
              {/* Name */}
              <div className="mb-6">
                <label htmlFor="name" className="block text-sm font-medium text-blue-900 mb-2">
                  お名前 <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full h-12 px-4 bg-gray-50 border border-transparent rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all"
                  placeholder="山田 太郎"
                />
              </div>

              {/* Company */}
              <div className="mb-6">
                <label htmlFor="company" className="block text-sm font-medium text-blue-900 mb-2">
                  御社名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full h-12 px-4 bg-gray-50 border border-transparent rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all"
                  placeholder="株式会社〇〇"
                />
              </div>

              {/* Email */}
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-blue-900 mb-2">
                  メールアドレス <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full h-12 px-4 bg-gray-50 border border-transparent rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all"
                  placeholder="example@email.com"
                />
              </div>

              {/* Category */}
              <div className="mb-6">
                <label htmlFor="category" className="block text-sm font-medium text-blue-900 mb-2">
                  お問い合わせ種別
                </label>
                <div className="relative">
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-12 px-4 bg-gray-50 border border-transparent rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all appearance-none cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="stablecoin">ステーブルコイン・決済に関するご相談</option>
                    <option value="creative">クラティブ・健康DXに関するご相談（自治体・医療機関向け）</option>
                    <option value="import">輸入商品・コーヒーに関するご相談</option>
                    <option value="construction">建設・防水コンクリートに関するご相談</option>
                    <option value="other">その他</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-600"></i>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-medium text-blue-900 mb-2">
                  お問い合わせ内容 <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all resize-y"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2 text-right">
                  {formData.message.length} / 500文字
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 text-center">
                    お問い合わせを受け付けました。ありがとうございます。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 text-center">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
